import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, RefreshCw, Edit } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { FilterGroupDto, CreateFilterGroupRequest, UpdateFilterGroupRequest } from '../../../../types/filterGroup';
import { filterGroupService } from '../../../../services/filterGroupService';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils';
import { useInlineEdit } from '../../../shared/table/hooks/useInlineEdit';
import type { ColumnDefinition, TableAction } from '../../../shared/table/types/table';
import { FilterGroupBulkUpdateModal } from './FilterGroupBulkUpdateModal';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface FilterGroupAppProps {
  onClose?: () => void;
  onBack?: () => void;
}

interface FilterGroupFormData {
  filterGroupID: string;
  filterGroupType: string;
  filterGroupDescr: string;
  filterGroupSortID: string;
}

const emptyFormData: FilterGroupFormData = {
  filterGroupID: '',
  filterGroupType: '',
  filterGroupDescr: '',
  filterGroupSortID: ''
};

export const FilterGroupApp: React.FC<FilterGroupAppProps> = ({ onClose, onBack }) => {
  const { t } = useTranslation();
  const [filterGroups, setFilterGroups] = useState<FilterGroupDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<FilterGroupFormData>(emptyFormData);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadFilterGroups();
  }, []);

  const loadFilterGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await filterGroupService.getCurrent();
      setFilterGroups(data);
    } catch (err) {
      console.error('Error loading filter groups:', err);
      setError(t('manager.modules.adminDelivery.filterGroup.errors.loadFailed', {}, 'Feil ved lasting av filtergrupper'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Inline edit config
  const inlineEditConfig = useInlineEdit<FilterGroupDto>({
    getRowId: (row) => row.id,
    onSave: async (originalRow, editedData) => {
      const request: UpdateFilterGroupRequest = {
        filterGroupDescr: (editedData.filterGroupDescr as string) ?? originalRow.filterGroupDescr,
        filterGroupSortID: editedData.filterGroupSortID as number | undefined
      };
      await filterGroupService.update(
        originalRow.companyNo,
        originalRow.filterGroupID,
        originalRow.filterGroupType,
        request
      );
    },
    onSaveSuccess: () => loadFilterGroups(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminDelivery.common.errors.saveFailed', {}, 'Feil ved lagring'))
  });

  // Selection handling
  const handleSelectionChange = useCallback((ids: (string | number)[], _data: FilterGroupDto[]) => {
    setSelectedIds(ids as number[]);
  }, []);

  const getRowId = useCallback((item: FilterGroupDto) => item.id, []);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert(t('manager.modules.adminDelivery.filterGroup.errors.selectAtLeastOne', {}, 'Velg minst én filtergruppe for vedlikehold'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length, t]);

  // Build selectedRows map for modal
  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  // Column factory
  const columnFactory = createColumnFactory<FilterGroupDto>();

  // Column definitions with editable fields
  const columns: ColumnDefinition<FilterGroupDto>[] = useMemo(() => [
    columnFactory.createNumberColumn('id', 'id', t('manager.modules.adminDelivery.filterGroup.columns.id', {}, 'ID'), {
      width: 80,
      sortable: true,
      filterType: 'text',
      editable: true
    }),
    columnFactory.createTextColumn('filterGroupID', 'filterGroupID', t('manager.modules.adminDelivery.filterGroup.fields.filterGroupID', {}, 'Filtergruppe ID'), {
      width: 160,
      sortable: true,
      filterType: 'text',
      editable: true
    }),
    columnFactory.createNumberColumn('filterGroupType', 'filterGroupType', t('common.fields.type', {}, 'Type'), {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('filterGroupDescr', 'filterGroupDescr', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 280,
      sortable: true,
      filterType: 'text',
      editable: true
    }),
    columnFactory.createNumberColumn('filterGroupSortID', 'filterGroupSortID', t('manager.modules.adminDelivery.filterGroup.fields.sortID', {}, 'Sortering'), {
      width: 110,
      sortable: true,
      filterType: 'text',
      editable: true
    }),
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.adminDelivery.common.columns.createdBy', {}, 'Opprettet av'), {
      width: 130,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.adminDelivery.common.columns.created', {}, 'Opprettet'), {
      width: 140,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.adminDelivery.common.columns.updatedBy', {}, 'Oppdatert av'), {
      width: 130,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminDelivery.common.columns.lastUpdate', {}, 'Sist oppdatert'), {
      width: 140,
      sortable: true,
      filterType: 'text'
    })
  ], [t]);

  // Table actions
  const tableActions: TableAction<FilterGroupDto>[] = useMemo(() => [
    {
      id: 'create-filtergroup',
      label: t('manager.modules.adminDelivery.filterGroup.create.title', {}, 'Ny filtergruppe'),
      icon: Plus,
      onClick: () => {
        setFormData(emptyFormData);
        setFormError(null);
        setSuccessMessage(null);
        setShowCreateModal(true);
      },
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminDelivery.common.maintain', { '0': selectedIds.length }, `Vedlikehold (${selectedIds.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit, t]);

  // Create form handlers
  const handleInputChange = (field: keyof FilterGroupFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.filterGroupID.trim()) {
      setFormError(t('manager.modules.adminDelivery.filterGroup.errors.idRequired', {}, 'Filtergruppe ID er påkrevd'));
      return;
    }
    if (!formData.filterGroupType.trim()) {
      setFormError(t('manager.modules.adminDelivery.filterGroup.errors.typeRequired', {}, 'Type er påkrevd'));
      return;
    }
    const typeNum = parseInt(formData.filterGroupType, 10);
    if (isNaN(typeNum)) {
      setFormError(t('manager.modules.adminDelivery.filterGroup.errors.typeMustBeNumber', {}, 'Type må være et tall'));
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      const createRequest: CreateFilterGroupRequest = {
        filterGroupID: formData.filterGroupID.trim(),
        filterGroupType: typeNum,
        filterGroupDescr: formData.filterGroupDescr.trim(),
        filterGroupSortID: formData.filterGroupSortID.trim() ? parseInt(formData.filterGroupSortID.trim(), 10) : undefined
      };

      await filterGroupService.create(createRequest);
      setSuccessMessage(t('manager.modules.adminDelivery.filterGroup.create.success', {}, 'Ny filtergruppe ble opprettet'));
      setTimeout(() => {
        loadFilterGroups();
        setShowCreateModal(false);
        setSuccessMessage(null);
      }, 1000);
    } catch (err) {
      console.error('Error creating filter group:', err);
      setFormError(err instanceof Error ? err.message : t('manager.modules.adminDelivery.filterGroup.errors.createFailed', {}, 'Feil ved opprettelse av filtergruppe'));
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{t('manager.modules.adminDelivery.filterGroup.title', {}, 'Filtergrupper')}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('manager.modules.adminDelivery.filterGroup.subtitle', {}, 'Administrer filtergrupper og filterkonfigurasjoner')}
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={filterGroups}
          columns={columns}
          loading={loading}
          error={error || undefined}
          config={{
            persistenceKey: 'filtergroup-table',
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            compactMode: true,
            stickyHeader: true,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          aria-label={t('manager.modules.adminDelivery.filterGroup.tableAriaLabel', {}, 'Filtergrupper tabell')}
        />
      </div>

      {/* Bulk Edit Side Panel */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <FilterGroupBulkUpdateModal
          selectedRows={selectedRowsMap}
          data={filterGroups}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadFilterGroups();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminDelivery.filterGroup.create.modalTitle', {}, 'Opprett ny filtergruppe')}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-4 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {formError}
                </div>
              )}
              {successMessage && (
                <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                  {successMessage}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminDelivery.filterGroup.fields.filterGroupID', {}, 'Filtergruppe ID')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.filterGroupID}
                  onChange={(e) => handleInputChange('filterGroupID', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  maxLength={50}
                  placeholder={t('manager.modules.adminDelivery.filterGroup.placeholders.filterGroupID', {}, 'F.eks. FILTER01')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.fields.type', {}, 'Type')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.filterGroupType}
                  onChange={(e) => handleInputChange('filterGroupType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common.fields.description', {}, 'Beskrivelse')}
                </label>
                <input
                  type="text"
                  value={formData.filterGroupDescr}
                  onChange={(e) => handleInputChange('filterGroupDescr', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  maxLength={100}
                  placeholder={t('manager.modules.adminDelivery.filterGroup.placeholders.description', {}, 'Beskrivelse av filtergruppen')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('manager.modules.adminDelivery.filterGroup.fields.sortID', {}, 'Sortering')}
                </label>
                <input
                  type="number"
                  value={formData.filterGroupSortID}
                  onChange={(e) => handleInputChange('filterGroupSortID', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('manager.modules.adminDelivery.filterGroup.placeholders.sortID', {}, 'Sorteringsrekkefølge')}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                  disabled={formLoading}
                >
                  {t('common.buttons.cancel', {}, 'Avbryt')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                  disabled={formLoading}
                >
                  {formLoading ? t('manager.modules.adminDelivery.common.creating', {}, 'Oppretter...') : t('common.buttons.create', {}, 'Opprett')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
